import { createContext, ReactNode, useCallback, useContext, useState } from 'react';

interface MenuVisibilityContextType {
  hamburgerMenuVisible: boolean;
  accountDropdownVisible: boolean;
  setHamburgerMenuVisible: (visible: boolean) => void;
  setAccountDropdownVisible: (visible: boolean) => void;
  toggleHamburgerMenu: () => void;
  toggleAccountDropdown: () => void;
}

const MenuVisibilityContext = createContext<MenuVisibilityContextType | undefined>(undefined);

const releaseMobileNavFocus = (): void => {
  const nav = document.getElementById('header-mobile-nav');
  const active = document.activeElement;
  if (!(nav && active instanceof HTMLElement && nav.contains(active))) {
    return;
  }

  document.getElementById('header-mobile-menu-toggle')?.focus();
};

export const MenuVisibilityProvider = ({ children }: { children: ReactNode }) => {
  const [hamburgerMenuVisible, setHamburgerMenuVisibleState] = useState(false);
  const [accountDropdownVisible, setAccountDropdownVisibleState] = useState(false);

  const setHamburgerMenuVisible = useCallback((visible: boolean) => {
    if (!visible) {
      releaseMobileNavFocus();
    }
    setHamburgerMenuVisibleState(visible);
    if (visible) {
      // Close account dropdown when hamburger menu opens
      setAccountDropdownVisibleState(false);
    }
  }, []);

  const setAccountDropdownVisible = useCallback((visible: boolean) => {
    setAccountDropdownVisibleState(visible);
    if (visible) {
      // Close hamburger menu when account dropdown opens
      releaseMobileNavFocus();
      setHamburgerMenuVisibleState(false);
    }
  }, []);

  const toggleHamburgerMenu = useCallback(() => {
    setHamburgerMenuVisible(!hamburgerMenuVisible);
  }, [hamburgerMenuVisible, setHamburgerMenuVisible]);

  const toggleAccountDropdown = useCallback(() => {
    setAccountDropdownVisible(!accountDropdownVisible);
  }, [accountDropdownVisible, setAccountDropdownVisible]);

  return (
    <MenuVisibilityContext.Provider
      value={{
        hamburgerMenuVisible,
        accountDropdownVisible,
        setHamburgerMenuVisible,
        setAccountDropdownVisible,
        toggleHamburgerMenu,
        toggleAccountDropdown,
      }}
    >
      {children}
    </MenuVisibilityContext.Provider>
  );
};

export const useMenuVisibility = () => {
  const context = useContext(MenuVisibilityContext);
  if (!context) {
    throw new Error('useMenuVisibility must be used within a MenuVisibilityProvider');
  }
  return context;
};
